import messages from "messages";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import moment from "moment-timezone";
// components
import IntervalEditor from "../interval-editor/IntervalEditor";
import AddTimeslot from "./components/add-timeslot";
import RemoveTimeslot from "./components/remove-timeslot";
import Checkbox from "components/checkbox/Checkbox";
// constants
import { EMPTY_TIME_SLOT } from "../schedule-creator-container/constants/empty-time-slot";
// styles
import styles from "./styles.module.scss";
// utils
import shouldShowAdd from "./utils/shouldShowAdd";
// import shouldShowApply from "./utils/shouldShowApply";
import shouldShowRemove from "./utils/shouldShowRemove";

type Props = {
  dateValue: number;
  dayTitle: string;
  intervals: ParsedAvailableTimePeriod["intervals"][number] | null;
  timezone: TimeZone;
  onApply: (arg: ParsedAvailableTimePeriod["intervals"][number]) => void;
  onApplyToAll: () => void;
};

/**
 * renders UI for editing available hours for each day
 */
const ForwardedDayRow = forwardRef<
  Pick<HTMLFormElement, "checkValidity" | "reset">,
  Props
>(function DayRow(
  {
    dateValue,
    dayTitle,
    intervals,
    timezone,
    // onApplyToAll,
    onApply,
  },
  ref,
) {
  const isEmpty = !intervals?.length;
  const isLastValid =
    isEmpty ||
    !Object.values(intervals?.[intervals.length - 1] || {}).includes("");

  const inputRefs = useRef<
    (Pick<HTMLFormElement, "checkValidity" | "reset"> | null)[]
  >([]);

  useImperativeHandle(ref, () => ({
    checkValidity: () => {
      const validities = inputRefs.current.map((el) => {
        if (!el) {
          return true;
        }

        return el.checkValidity();
      });

      return validities.every((el) => el);
    },
    reset: () =>
      inputRefs.current.forEach((el) => {
        if (!el) {
          return true;
        }
        return el.reset();
      }),
  }));

  const [display, setDisplay] = useState(false);
  const showActionBtns = (e) => {
    e.preventDefault();
    setDisplay(true);
  };

  const hideActionBtns = (e) => {
    e.preventDefault();
    setDisplay(false);
  };

  return (
    <Row
      as={"li"}
      noGutters
      onMouseEnter={(e) => showActionBtns(e)}
      onMouseLeave={(e) => hideActionBtns(e)}
      className={styles["day-row"]}
    >
      <Col xs={2} className={styles["day-row__title"]} as={"label"}>
        <Checkbox
          onChange={(e) => {
            const date = moment(dateValue).tz(timezone);
            return onApply(
              e.target.checked
                ? [
                    {
                      start_time: date.clone().hour(9).toISOString(),
                      end_time: date.clone().hour(17).toISOString(),
                    },
                  ]
                : null,
            );
          }}
          checked={intervals !== null}
          label={dayTitle.toUpperCase()}
          aria-label={dayTitle}
          labelClassName={styles["day-row__checkbox-label"]}
        />
      </Col>
      <Col>
        {intervals === null ? (
          <div
            className={styles["day-row__unavailable"]}
            ref={() => {
              inputRefs.current = [];
            }}
          >
            {messages.unavailable}
          </div>
        ) : (
          (!intervals.length
            ? ([EMPTY_TIME_SLOT] as NonNullable<typeof intervals>)
            : intervals
          ).map((interval, index) => {
            const startTime = !interval.start_time
              ? ""
              : moment(interval.start_time).tz(timezone);
            const endTime = !interval.end_time
              ? ""
              : moment(interval.end_time).tz(timezone);

            return (
              <Row
                key={`interval-${index}`}
                className={styles["day-row__interval-container"]}
              >
                <div className={styles["day-row__interval"]}>
                  <IntervalEditor
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    dateValue={dateValue}
                    interval={{
                      from: !startTime ? "" : startTime.toISOString(),
                      to: !endTime ? "" : endTime.toISOString(),
                    }}
                    onChange={({ from, to }) => {
                      const res = {
                        start_time: from || interval.start_time,
                        end_time: to || interval.end_time,
                      };

                      return onApply([
                        ...(intervals || []).slice(0, index),
                        res,
                        ...(intervals || []).slice(index + 1),
                      ]);
                    }}
                    // error={intervalsErrors[index]}
                  />
                </div>

                {display && (
                  <div className={styles["day-row__btns"]}>
                    {shouldShowRemove(isEmpty) && (
                      <RemoveTimeslot
                        className={styles["day-row__icon-button"]}
                        onClick={() => {
                          const nextIntervals = [
                            ...(intervals || []).slice(0, index),
                            ...(intervals || []).slice(index + 1),
                          ];
                          // uncheck the day if no valid intervals
                          onApply(
                            nextIntervals.length === 0 ? null : nextIntervals,
                          );
                        }}
                      />
                    )}
                    {shouldShowAdd(
                      index,
                      (intervals || []).length,
                      isLastValid,
                    ) && (
                      <AddTimeslot
                        className={styles["day-row__icon-button"]}
                        onClick={() =>
                          onApply([...(intervals || []), EMPTY_TIME_SLOT])
                        }
                      />
                    )}
                    {/* TODO: hide it for now
                     * @see https://www.figma.com/file/27ZhE2NHHVAfrPGw3d0fhs?node-id=6729:234465#250652759
                     */}
                    {/* {shouldShowApply(
                    index,
                    (intervals || []).length,
                    isLastValid,
                  ) && (
                    <Button
                      variant="link"
                      className="ml-auto fs-14 border-0"
                      onClick={() => onApplyToAll()}
                    >
                      {messages.applyToAll}
                    </Button>
                  )} */}
                  </div>
                )}
              </Row>
            );
          })
        )}
      </Col>
    </Row>
  );
});

export default ForwardedDayRow;
